import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Clock } from 'lucide-react';
import { calendarService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { CalendarServiceType } from '../types/calendar';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ServiceTypeList from '../components/calendar/ServiceTypeList';
import ServiceTypeForm from '../components/calendar/ServiceTypeForm';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function CalendarServiceTypes() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingServiceType, setEditingServiceType] = useState<CalendarServiceType | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: serviceTypes, isLoading } = useQuery({
    queryKey: ['calendar-service-types'],
    queryFn: () => calendarService.getServiceTypes(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<CalendarServiceType>) => calendarService.createServiceType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-service-types'] });
      messageHandler.success('Tipo de serviço criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CalendarServiceType> }) =>
      calendarService.updateServiceType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-service-types'] });
      messageHandler.success('Tipo de serviço atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => calendarService.deleteServiceType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-service-types'] });
      messageHandler.success('Tipo de serviço excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      duration_minutes: parseInt(formData.get('duration_minutes') as string, 10),
    };

    if (editingServiceType) {
      updateMutation.mutate({ id: editingServiceType.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (serviceType: CalendarServiceType) => {
    messageHandler.confirm(
      'Excluir Tipo de Serviço',
      'Tem certeza que deseja excluir este tipo de serviço? Esta ação não pode ser desfeita.',
      () => deleteMutation.mutate(serviceType.id)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingServiceType(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Tipos de Serviço"
          description="Gerencie os tipos de serviço disponíveis para agendamento."
          actions={
            <Button
              onClick={() => {
                setEditingServiceType(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Tipo de Serviço
            </Button>
          }
        />

        <ServiceTypeList
          serviceTypes={serviceTypes || []}
          onEdit={serviceType => {
            setEditingServiceType(serviceType);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingServiceType ? 'Editar Tipo de Serviço' : 'Novo Tipo de Serviço'}
        >
          <ServiceTypeForm
            serviceType={editingServiceType}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default CalendarServiceTypes;
